import React, { ReactNode, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SecondaryButton from "../buttons/SecondaryButton";
import Button from "../buttons/Button";
import TextArea from "../inputfields/TextArea";
import PercentageInput from "../inputfields/PercentageInput";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "../inputfields/InputFieldContent";

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subText?: ReactNode;
  children?: ReactNode;
  primaryButtonText: string;
  secondaryButtonText?: string;
  handelSubmit: (text?: string) => void;
  textArea?: boolean;
  buttonColor?: string;
  buttonLoading?: boolean;
  secondaryButtonLoading?: boolean;
  buttonWidth?: string;
  primaryButtonClassName?: string;
  input?: {
    labelName: string;
    id: string;
  };
  reverseButton?: boolean | string;
  maxWidth?: string;
  note?: string;
  secondaryButton?: boolean;
  onHandleClose?: () => void;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  secondaryWidth?: string;
  noButtons?: boolean;
  bordered?: boolean;
  titleClassName?: string;
  secondaryButtonClassName?: string;
  /** Extra classes on the modal card, e.g. to override the default padding. */
  className?: string;
  /** Extra classes on the action-button row. */
  footerClassName?: string;
}

const CustomModal: React.FC<CustomModalProps> = ({ buttonLoading, buttonWidth, textArea, reverseButton, onHandleClose, isOpen, note, maxWidth, onClose, buttonColor, title, subText, children, primaryButtonText, secondaryButtonText, handelSubmit, primaryButtonClassName, input, secondaryButton = true,
  secondaryButtonLoading, closeOnOverlayClick = true, noButtons = false, showCloseButton = false, primaryButtonDisabled = false, secondaryButtonDisabled = false, secondaryWidth, bordered = false, titleClassName, secondaryButtonClassName, className, footerClassName }) => {
  const [text, setText] = useState<string>("");
  const [textError, setTextError] = useState<string>('');
  const { translate, notEnglish } = useTranslation(inputFieldContent)
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose(); // Close the modal when clicking the overlay only if closeOnOverlayClick is true
    }
  };

  const handleModalContentClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation(); // Prevent click event from bubbling up to the overlay
  };

  const handleSubmitButtonClick = () => {
    if (textArea && text === "") {
      setTextError("This field is required");
    } else if (input && text === "") {
      setTextError("This field is required");
    } else {
      handelSubmit(text);
    }
  }
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/50 z-9999"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick} // Listen for clicks on the overlay
          dir={notEnglish ? "rtl" : "ltr"}
        >
          <motion.div
            className={`bg-white rounded-lg shadow-lg p-6 ${maxWidth || "max-w-md"
              } w-full relative ${bordered ? "border border-s_light" : ""} ${className || ""}`}
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
            data-testid= "custom-modal"
            onClick={handleModalContentClick} // Add click handler to modal content
          >
            {showCloseButton && (
              <button
                onClick={onClose}
                className="absolute top-6 right-4 text-gray-500 hover:text-gray-700 focus:outline-hidden"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            {title && (
              <h2 className={titleClassName || "text-heading5 font-medium pb-2 mb-2 text-primary_text border-b border-b-outline"}>
                {title}
              </h2>
            )}
            {typeof subText === "string" && subText.trim().startsWith("{") ? (
              <pre
                className="text-sm bg-white p-2 rounded-sm overflow-x-auto whitespace-pre-wrap"
                dangerouslySetInnerHTML={{
                  __html: subText
                    .replace(/&/g, "&amp;") // escape HTML first
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;")
                    // Keys in blue: "key":
                    .replace(/\"([^"]+)\":/g, '<span class="text-blue-500">"$1"</span>:')
                    // String values in green: "value"
                    .replace(/: \"([^"]*)\"/g, ': <span class="text-green-500">"$1"</span>')
                    // Numbers in green
                    .replace(/: (\d+)/g, ': <span class="text-green-500">$1</span>')
                }}
              />
            ) : (
              <h2 className="subTitle text-secondary_text wrap-break-word">{subText}</h2>
            )}

            {note && <h2 className="subTitle text-secondary_text">{note}</h2>}
            {input && (
              <PercentageInput
                label={input.labelName}
                initialValue={text}
                setText={setText}
                setTextError={setTextError}
                errorMessage={textError}
                onChange={() => {
                  handleSubmitButtonClick;
                }}
                required={true}
              />
            )}
            {textArea && (
              <TextArea
                text={text}
                setText={setText}
                textError={textError}
                setTextError={setTextError}
              />
            )}

            <div>{children}</div>
            <div
              className={`flex ${textArea || children ? "mt-1" : "mt-6"} ${reverseButton || ""
                } gap-6 ${footerClassName || ""}`}
            >
              {secondaryButton !== false && (
                <SecondaryButton
                  buttonName={secondaryButtonText || ""}
                  className={`px-4 py-2 h-fit ${secondaryWidth} ${secondaryButtonClassName || ""}`}
                  handleClick={onHandleClose ? onHandleClose : onClose}
                  testId={`${secondaryButtonText?.toLowerCase()}-btn`}
                  buttonDisabled={buttonLoading || secondaryButtonDisabled}
                  loading={secondaryButtonLoading}
                />
              )}
              {!noButtons && <Button
                buttonName={primaryButtonText}
                className={`py-2.5 ${primaryButtonClassName} px-4 rounded-[6px] `}
                textSize={"text-large"}
                buttonOnclick={handleSubmitButtonClick}
                testId={`${primaryButtonText.toLowerCase()}-btn`}
                loading={buttonLoading}
                bgColor={buttonColor}
                buttonWidth={buttonWidth}
                buttonHight="h-[42px]"
                buttonDisabled={buttonLoading || primaryButtonDisabled}
              />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CustomModal;
